import { Button, Modal } from "@/components";
import styles from "./DeleteModelVersionModal.module.scss";

export type DeleteModelVersionPreview =
  | {
      kind: "rollback";
      versionLabel: string;
      restoreToLabel: string;
    }
  | {
      kind: "remove_species";
      versionLabel: string;
    }
  | {
      kind: "version_only";
      versionLabel: string;
    };

type DeleteModelVersionModalProps = {
  isOpen: boolean;
  preview: DeleteModelVersionPreview | null;
  speciesDisplayName: string;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
};

export const DeleteModelVersionModal = ({
  isOpen,
  preview,
  speciesDisplayName,
  onClose,
  onConfirm,
  isDeleting,
}: DeleteModelVersionModalProps) => {
  if (!preview) return null;

  const heading =
    preview.kind === "remove_species"
      ? "Remove model from the application?"
      : preview.kind === "rollback"
        ? "Delete live version?"
        : "Delete version data?";

  const description =
    preview.kind === "rollback" ? (
      <>
        Version <strong>{preview.versionLabel}</strong> and its files will be
        permanently deleted. The catalog will be rolled back to version{" "}
        <strong>{preview.restoreToLabel}</strong>.
      </>
    ) : preview.kind === "remove_species" ? (
      <>
        There is no previous published version.{" "}
        <strong>{speciesDisplayName}</strong> (including version{" "}
        <strong>{preview.versionLabel}</strong> and all other versions) will be
        completely removed from the application. This cannot be undone.
      </>
    ) : (
      <>
        Version <strong>{preview.versionLabel}</strong> and its files will be
        permanently deleted. This cannot be undone.
      </>
    );

  return (
    <Modal isOpen={isOpen} onClose={isDeleting ? () => {} : onClose}>
      <div className={styles["comp"]}>
        <div>
          <div className={styles["comp__heading"]}>{heading}</div>
          <p className={styles["comp__description"]}>{description}</p>
          <div className={styles["comp__label"]} title={speciesDisplayName}>
            {speciesDisplayName}
            {" · "}
            {preview.versionLabel}
          </div>
        </div>

        <div className={styles["comp__actions"]}>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting…" : "Delete"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
